import React, { useContext, useEffect, useLayoutEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Input, Textarea, CustomSelect, ConfirmModal, LoadingButton, DialogShell, DialogHeader, DialogFooter, dialogButtonClass, FormFieldWithError } from '@redpanda/ui';
import { createTimeEntry, updateTimeEntry, getProjects, getProjectTasks } from '../services/api';

import { toDateInput, toISOString, getTodayDateInput } from '../utils/dateUtils';
import { notifyDataChanged } from '../utils/dataEvents';
import { isAdmin, useNumberInput, useUnsavedChangesWarning } from '@redpanda/utils';
import { UserContext } from '../context/UserContext';
import { useMembers } from '../stores/lookupStore';
import { PMTimeEntry } from '../types/pm';

interface TimeEntryDialogProps {
  isOpen: boolean;
  onClose: () => void;
  entry?: PMTimeEntry;
  /** 從專案或任務開啟時固定專案（不顯示專案選單） */
  projectId?: string;
  /** 從任務開啟時固定任務 */
  taskId?: string;
  /** 存檔後回傳（任務對話框用來更新實際工時） */
  onSaved?: (entry: PMTimeEntry) => void;
}

interface FormData {
  projectId: string;
  userId: string;
  taskId: string;
  date: string;
  hours: number | '';
  note: string;
}

/**
 * 記錄工時（照 PaymentDialog）：專案、任務（選填）、日期、時數、說明
 * 記在自己名下；存檔後任務的實際工時由後端加總
 */
const TimeEntryDialog: React.FC<TimeEntryDialogProps> = ({ isOpen, onClose, entry, projectId: fixedProjectId, taskId: fixedTaskId, onSaved }) => {
  const { t } = useTranslation(['translation', 'pages']);
  const { register, handleSubmit, formState: { errors, isDirty }, setValue, reset, watch } = useForm<FormData>();
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<{ id: string; name: string }[]>([]);
  const [tasks, setTasks] = useState<{ id: string; title: string; status: string }[]>([]);
  const hoursInput = useNumberInput('decimal');
  const isEditMode = !!entry;
  const { user } = useContext(UserContext)!;
  const members = useMembers();
  const canLogForOthers = isAdmin(user?.role) && !isEditMode; // 管理員代成員補登
  const projectLocked = !!fixedProjectId || isEditMode; // 編輯時不能換專案（後端也不接受）

  const actualHandleClose = () => { reset(); onClose(); };
  const { handleClose, showConfirm, handleConfirmClose, handleCancelClose } = useUnsavedChangesWarning(isDirty, actualHandleClose);

  const watchedProjectId = watch('projectId', '');
  const watchedTaskId = watch('taskId', '');
  const watchedUserId = watch('userId', '');

  useLayoutEffect(() => {
    if (!isOpen) return;
    reset(entry
      ? { projectId: entry.projectId, userId: entry.userId, taskId: entry.taskId || '', date: toDateInput(entry.date), hours: entry.hours, note: entry.note || '' }
      : { projectId: fixedProjectId || '', userId: user?.id || '', taskId: fixedTaskId || '', date: getTodayDateInput(), hours: '', note: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, entry, fixedProjectId, fixedTaskId, reset]);

  // 可選的專案（未封存）；固定專案時不用載
  useEffect(() => {
    if (!isOpen || projectLocked) return;
    getProjects(1, { pageSize: 200 })
      .then(({ data }) => setProjects((data.data || []).map((p: any) => ({ id: p.id, name: p.name }))))
      .catch(error => console.error('Failed to load projects:', error));
  }, [isOpen, projectLocked]);

  // 選了專案後載入該專案的任務
  useEffect(() => {
    if (!isOpen || !watchedProjectId || fixedTaskId) return;
    let cancelled = false;
    getProjectTasks(watchedProjectId)
      .then(({ data }) => !cancelled && setTasks(data.tasks))
      .catch(error => console.error('Failed to load tasks:', error));
    return () => { cancelled = true; };
  }, [isOpen, watchedProjectId, fixedTaskId]);

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      const payload = {
        taskId: data.taskId || null,
        date: toISOString(data.date)!,
        hours: parseFloat(String(data.hours)),
        note: data.note.trim() || null,
      };
      const { data: saved } = isEditMode
        ? await updateTimeEntry(entry.id, payload)
        : await createTimeEntry({ ...payload, projectId: data.projectId, ...(canLogForOthers && data.userId && { userId: data.userId }) });
      toast.success(t(isEditMode ? 'common.messages.updateSuccess' : 'common.messages.createSuccess'));
      notifyDataChanged('timeEntries');
      if (saved.taskId || entry?.taskId) notifyDataChanged('tasks'); // 任務實際工時跟著變
      onSaved?.(saved);
      actualHandleClose();
    } catch (error: any) {
      toast.error(error.response?.data?.error || t('common.messages.operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <DialogShell onClose={handleClose} closeDisabled={loading} className="max-w-lg">
        <DialogHeader
          title={t(isEditMode ? 'pmTime.editTitle' : 'pmTime.addTitle', { ns: 'pages' })}
          onClose={handleClose}
          closeDisabled={loading}
          closeLabel={t('common.actions.close')}
        />
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="p-6 space-y-5">
            {!projectLocked && (
              <FormFieldWithError label={t('pmTime.fields.project', { ns: 'pages' })} required error={errors.projectId?.message} htmlFor="projectId">
                <CustomSelect
                  id="projectId"
                  {...register('projectId', { required: t('pmTime.validation.projectRequired', { ns: 'pages' }) })}
                  value={watchedProjectId}
                  onChange={(value) => {
                    setValue('projectId', value, { shouldDirty: true, shouldValidate: true });
                    setValue('taskId', '');
                  }}
                  placeholder={t('pmTime.selectProject', { ns: 'pages' })}
                  options={projects.map(p => ({ value: p.id, label: p.name }))}
                  searchable={projects.length > 8}
                  searchPlaceholder={t('pmTime.searchProject', { ns: 'pages' })}
                />
              </FormFieldWithError>
            )}

            {!fixedTaskId && (
              <FormFieldWithError label={t('pmTime.fields.task', { ns: 'pages' })} htmlFor="taskId">
                <CustomSelect
                  id="taskId"
                  value={watchedTaskId}
                  onChange={(value) => setValue('taskId', value, { shouldDirty: true })}
                  disabled={!watchedProjectId}
                  options={[
                    { value: '', label: t('pmTime.noTask', { ns: 'pages' }) },
                    ...tasks.map(task => ({ value: task.id, label: task.title })),
                  ]}
                  searchable={tasks.length > 8}
                  searchPlaceholder={t('pmTime.searchTask', { ns: 'pages' })}
                />
              </FormFieldWithError>
            )}

            {canLogForOthers && members.length > 1 && (
              <FormFieldWithError label={t('pmTime.fields.member', { ns: 'pages' })} htmlFor="userId">
                <CustomSelect
                  id="userId"
                  value={watchedUserId}
                  onChange={(value) => setValue('userId', value, { shouldDirty: true })}
                  options={members.map(m => ({ value: m.id, label: m.name || m.email }))}
                  searchable={members.length > 8}
                />
              </FormFieldWithError>
            )}

            <div className="grid grid-cols-2 gap-4">
              <FormFieldWithError label={t('pmTime.fields.date', { ns: 'pages' })} required error={errors.date?.message} htmlFor="date">
                <Input
                  type="date"
                  id="date"
                  {...register('date', { required: t('pmTime.validation.dateRequired', { ns: 'pages' }) })}
                  variant={errors.date ? 'error' : 'default'}
                />
              </FormFieldWithError>
              <FormFieldWithError label={t('pmTime.fields.hours', { ns: 'pages' })} required error={errors.hours?.message} htmlFor="hours">
                <Input
                  type="text"
                  id="hours"
                  {...hoursInput.bind(register('hours', {
                    required: t('pmTime.validation.hoursRequired', { ns: 'pages' }),
                    validate: value => {
                      const n = parseFloat(String(value));
                      return (n > 0 && n <= 24) || t('pmTime.validation.hoursRange', { ns: 'pages' });
                    },
                  }))}
                  variant={errors.hours ? 'error' : 'default'}
                  placeholder="1.5"
                />
              </FormFieldWithError>
            </div>

            <FormFieldWithError label={t('pmTime.fields.note', { ns: 'pages' })} error={errors.note?.message} htmlFor="note">
              <Textarea
                id="note"
                rows={2}
                {...register('note', { maxLength: { value: 500, message: t('common.forms.validation.maxLength', { length: 500 }) } })}
                placeholder={t('pmTime.notePlaceholder', { ns: 'pages' })}
              />
            </FormFieldWithError>
          </div>

          <DialogFooter>
            <button type="button" onClick={handleClose} disabled={loading} className={dialogButtonClass.cancel}>
              {t('common.actions.cancel')}
            </button>
            <LoadingButton
              type="submit"
              loading={loading}
              isEditMode={isEditMode}
              customText={{ create: t('common.actions.create'), createLoading: t('common.status.creating') }}
              t={t}
            />
          </DialogFooter>
        </form>
      </DialogShell>

      <ConfirmModal
        isOpen={showConfirm}
        onClose={handleCancelClose}
        onConfirm={handleConfirmClose}
        title={t('components.modal.unsavedChanges.title')}
        message={t('components.modal.unsavedChanges.message')}
        confirmText={t('components.modal.unsavedChanges.confirmButton')}
        cancelText={t('components.modal.unsavedChanges.cancelButton')}
        t={t}
      />
    </>
  );
};

export default TimeEntryDialog;
